import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    OnChanges,
    inject,
} from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import {
    OrgDraft,
    OrgDraftPart,
    OrgDraftProgress,
    OrgDraftProgressStage,
    OrgFeature,
    StaffResolutionStatus,
    StaffStageReport,
    StaffStageRowOutcome,
} from '@newgenus/common';
import { Subscription } from 'rxjs';
import { OrgWizardStateService } from '../services/org-wizard-state.service';

interface BundleSummary {
    name: string;
    selectedCount: number;
    totalCount: number;
    locked: boolean;
}

interface ProgressStageRow {
    stage: OrgDraftProgressStage;
    label: string;
    /** Display order (1..7 for normal stages; 8 for done; -1 for error). */
    ordinal: number;
}

/** Linear, ordered list of stages the orchestrator reports through `orgDrafts/{key}.progress.stage`. */
const STAGE_ORDER: ProgressStageRow[] = [
    { stage: 'org', label: 'Creating organization document', ordinal: 1 },
    { stage: 'features', label: 'Copying features', ordinal: 2 },
    { stage: 'policies', label: 'Writing default policies', ordinal: 3 },
    { stage: 'parts', label: 'Building organization structure', ordinal: 4 },
    { stage: 'hats', label: 'Creating CEO hat', ordinal: 5 },
    { stage: 'staff', label: 'Staging staff invitations', ordinal: 6 },
    { stage: 'finalising', label: 'Linking you as owner', ordinal: 7 },
];

/**
 * Step 6 — Review.
 *
 * Renders collapsible summaries of each preceding step and surfaces the `Create`
 * button. Submission is delegated upward (the wizard shell owns the
 * {@link OrgCreationService} call so it can manage navigation on success).
 *
 * Plan & Features panel (R9.1) — fetches the global `features/` collection
 * once, joins it against the draft's selected feature keys, and surfaces a
 * per-bundle selection summary so the review reflects what Step 5 actually
 * captured (rather than the legacy "default features" placeholder).
 */
@Component({
    selector: 'newgenus-org-creation-step-review',
    templateUrl: './step-6-review.component.html',
    styleUrls: ['./step-6-review.component.scss'],
    standalone: false,
})
export class StepReviewComponent implements OnInit, OnChanges, OnDestroy {
    private db = inject(AngularFirestore);
    public state = inject(OrgWizardStateService);

    /** Required so the component can subscribe to `orgDrafts/{key}.progress` (R9.3). */
    @Input() public draftKey: string | null = null;
    @Input() public submitting = false;
    @Input() public errorMessage: string | null = null;
    @Output() public submitDraft = new EventEmitter<void>();

    public featuresLoading = true;
    public featuresLoadError: string | null = null;

    /** Cached features so the per-bundle summary stays cheap on change-detection. */
    public allFeatures: OrgFeature[] = [];

    /** Live progress doc snapshot. Null until the first orchestrator update lands. */
    public progress: OrgDraftProgress | null = null;

    /**
     * Phase B.2b — per-row staff stage outcomes written by the orchestrator
     * once the staff stage finishes. Drives the summary block under the
     * progress bar (“N of M staged, K failed…”).
     */
    public staffStageReport: StaffStageReport | null = null;

    /** Snapshot of the linear stage list, frozen for template iteration. */
    public readonly stages = STAGE_ORDER;

    private sub = new Subscription();
    private progressSub?: Subscription;

    public ngOnInit(): void {
        this.sub.add(
            this.db
                .collection<OrgFeature>('features')
                .get()
                .subscribe({
                    next: (snap) => {
                        this.allFeatures = snap.docs.map((d) => ({
                            ...(d.data() as OrgFeature),
                            key: d.id,
                        }));
                        this.featuresLoading = false;
                    },
                    error: (err) => {
                        this.featuresLoading = false;
                        this.featuresLoadError = err?.message ?? 'Could not load feature catalogue';
                    },
                })
        );
    }

    public ngOnDestroy(): void {
        this.sub.unsubscribe();
        this.progressSub?.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // Start watching the draft's progress doc as soon as the user submits.
        if (changes['submitting'] && this.submitting && this.draftKey && !this.progressSub) {
            this.startWatchingProgress(this.draftKey);
        }
        if (changes['draftKey'] && !this.draftKey) {
            this.progressSub?.unsubscribe();
            this.progressSub = undefined;
            this.progress = null;
        }
    }

    private startWatchingProgress(draftKey: string): void {
        this.progress = null;
        this.staffStageReport = null;
        this.progressSub?.unsubscribe();
        this.progressSub = this.db
            .collection<OrgDraft>('orgDrafts')
            .doc(draftKey)
            .valueChanges()
            .subscribe({
                next: (draft) => {
                    this.progress = draft?.progress ?? null;
                    this.staffStageReport = draft?.staffStageReport ?? null;
                },
                error: (err) => {
                    console.warn('[StepReviewComponent] progress subscription error:', err);
                },
            });
    }

    public onSubmit(): void {
        if (this.submitting) return;
        this.submitDraft.emit();
    }

    // ─────────────────────────────────────────────────────────────────
    // Phase B.2b — autoSendInvitations toggle (§6.4 default OFF)
    // ─────────────────────────────────────────────────────────────────

    public setAutoSendInvitations(enabled: boolean): void {
        this.state.dispatch({ autoSendInvitations: enabled });
    }

    // ─────────────────────────────────────────────────────────────────
    // Phase B.2b — staff stage report helpers
    // ─────────────────────────────────────────────────────────────────

    public get stagedSuccessCount(): number {
        if (!this.staffStageReport) return 0;
        return this.staffStageReport.rows.filter(
            (r) =>
                r.outcome === 'invitation-created' ||
                r.outcome === 'skipped-already-invited' ||
                r.outcome === 'skipped-already-staged'
        ).length;
    }

    public get stagedFailedRows(): StaffStageRowOutcome[] {
        if (!this.staffStageReport) return [];
        return this.staffStageReport.rows.filter((r) => r.outcome === 'failed' || r.outcome === 'skipped-unresolved');
    }

    public outcomeLabel(outcome: StaffStageRowOutcome['outcome']): string {
        switch (outcome) {
            case 'invitation-created':
                return 'Invitation sent';
            case 'skipped-already-invited':
                return 'Already invited — expiry extended';
            case 'skipped-already-staged':
                return 'Already pre-staged';
            case 'skipped-unresolved':
                return 'Skipped — email unresolved';
            case 'failed':
                return 'Failed';
        }
    }

    // ─────────────────────────────────────────────────────────────────────
    // Staff card helpers (mirrors the badge logic from step-4-staff)
    // ─────────────────────────────────────────────────────────────────────

    public postLabelFor(parts: OrgDraftPart[], postKey: string | undefined): string {
        if (!postKey) return '(unassigned)';
        const p = parts.find((x) => x.partKey === postKey);
        return p ? p.name : '(removed)';
    }

    /**
     * Plan addendum 19 — surface the post the CEO hat will be attached to,
     * so the user gets a last-chance sanity check before submitting.
     * Falls back to "(none — auto-pick on submit)" for legacy drafts that
     * never visited Step 3's crown UI; the backend orchestrator's depth-1
     * safety net will pick lowest-depth post in that case.
     */
    public ceoPostLabel(parts: OrgDraftPart[]): string {
        const crowned = parts.find((p) => p.partType === 'post' && (p as { isCeoPost?: boolean }).isCeoPost === true);
        if (crowned) return crowned.name?.trim() || '(unnamed)';
        return '(none — auto-pick on submit)';
    }

    public statusIcon(status: StaffResolutionStatus): string {
        switch (status) {
            case 'existing-user':
                return 'verified_user';
            case 'invitable':
                return 'mail_outline';
            case 'invalid':
                return 'error_outline';
            case 'unresolved':
            default:
                return 'help_outline';
        }
    }

    public statusLabel(status: StaffResolutionStatus): string {
        switch (status) {
            case 'existing-user':
                return 'Existing user — will be added pending approval';
            case 'invitable':
                return 'New user — will be emailed an invitation';
            case 'invalid':
                return 'Invalid email — fix on Step 4';
            case 'unresolved':
            default:
                return 'Pending check';
        }
    }
    // ─────────────────────────────────────────────────────────────────────
    // R9.3 — Progress timeline helpers
    // ─────────────────────────────────────────────────────────────────────

    /** Live ordinal index for the orchestrator's current stage; `0` until the first ping. */
    public get currentOrdinal(): number {
        if (!this.progress) return 0;
        if (this.progress.stage === 'done') return STAGE_ORDER.length + 1;
        if (this.progress.stage === 'error') return -1;
        return STAGE_ORDER.find((s) => s.stage === this.progress!.stage)?.ordinal ?? 0;
    }

    public stageStatus(row: ProgressStageRow): 'pending' | 'active' | 'done' {
        if (this.currentOrdinal === -1) {
            // Errored — leave non-passed rows as pending so the user sees how far we got.
            return row.ordinal < (this.progress?.current ?? 0) ? 'done' : 'pending';
        }
        if (this.currentOrdinal === 0) return 'pending';
        if (row.ordinal < this.currentOrdinal) return 'done';
        if (row.ordinal === this.currentOrdinal) return 'active';
        return 'pending';
    }

    public stageIcon(row: ProgressStageRow): string {
        const status = this.stageStatus(row);
        if (status === 'done') return 'check_circle';
        if (status === 'active') return 'sync';
        return 'radio_button_unchecked';
    }

    public get isErrored(): boolean {
        return this.progress?.stage === 'error';
    }

    public get isDone(): boolean {
        return this.progress?.stage === 'done';
    }

    /** Ratio used by the linear progress bar; defaults to indeterminate while submitting. */
    public get progressFraction(): number | null {
        if (!this.progress) return null;
        const total = this.progress.total || STAGE_ORDER.length;
        return Math.min(1, Math.max(0, (this.progress.current || 0) / total));
    }

    /** True when the user has explicitly chosen at least one feature in Step 5. */
    public get hasFeatureSelection(): boolean {
        return (this.state.snapshot?.features?.length ?? 0) > 0;
    }

    /** Count of features the user has selected (0 means "use orchestrator default = all"). */
    public get selectedCount(): number {
        return this.state.snapshot?.features?.length ?? 0;
    }

    public get totalCount(): number {
        return this.allFeatures.length;
    }

    /** Number of always-on system features that will be added on top of the user's selection. */
    public get systemFeatureCount(): number {
        return this.allFeatures.filter((f) => f.systemFeature).length;
    }

    /** Strap line displayed in the `mat-panel-description`. */
    public get featureSummary(): string {
        if (this.featuresLoading) return 'Loading…';
        if (this.featuresLoadError) return 'Free plan';
        if (!this.hasFeatureSelection) {
            return `Free plan · all ${this.totalCount} features`;
        }
        const sel = this.selectedCount;
        const total = this.totalCount;
        return `Free plan · ${sel} / ${total} features selected`;
    }

    /** Per-bundle breakdown for the expanded panel body. */
    public get bundleSummaries(): BundleSummary[] {
        if (this.featuresLoading || this.featuresLoadError || !this.allFeatures.length) {
            return [];
        }
        const selected = new Set(this.state.snapshot?.features ?? []);
        const byGroup = new Map<string, BundleSummary>();
        let systemTotal = 0;
        let systemSelected = 0;

        for (const f of this.allFeatures) {
            if (f.systemFeature) {
                systemTotal++;
                // System features are always copied by the orchestrator regardless
                // of selection — count them as selected for the user-facing summary.
                systemSelected++;
                continue;
            }
            const groupKey = (f.featureGroups?.[0] ?? 'Other').trim() || 'Other';
            if (!byGroup.has(groupKey)) {
                byGroup.set(groupKey, {
                    name: this.titleCase(groupKey),
                    selectedCount: 0,
                    totalCount: 0,
                    locked: false,
                });
            }
            const bundle = byGroup.get(groupKey)!;
            bundle.totalCount++;
            // When the user has not yet visited Step 5, draft.features is empty
            // and the orchestrator will copy everything — reflect that here.
            const isSelected = this.hasFeatureSelection ? selected.has(f.key) : true;
            if (isSelected) bundle.selectedCount++;
        }

        const ordered = Array.from(byGroup.values()).sort((a, b) => a.name.localeCompare(b.name));
        if (systemTotal > 0) {
            ordered.unshift({
                name: 'System (always on)',
                selectedCount: systemSelected,
                totalCount: systemTotal,
                locked: true,
            });
        }
        return ordered;
    }

    private titleCase(s: string): string {
        return s
            .replace(/[-_]+/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .trim();
    }
}
